import { atom, selector, selectorFamily } from 'recoil'
import { keyFactory, mockableSelector } from 'src/utils/recoil'
import { vendorBrandsClient, brandLogosClient, brandsClient } from 'src/clients'
import { Brand } from 'src/common-services/clients/vendors/brands'
import {
  AlphamapBrandLogo,
  BASE_IMAGE_URL,
  BrandLogosInRadiusRequest,
  BrandLogosInRadiusResponse,
} from 'src/common-services/types/brand-logo-types'
import { AMBrand } from 'src/common-services'
import { browserLogger } from 'src/utils/browser-logger'

const key = keyFactory('brands')

export const brand = selectorFamily<Brand | undefined, string | undefined>({
  key: key('brand'),
  get: (domain) => async () => {
    if (!domain) {
      return undefined
    }
    return vendorBrandsClient.getBrand({ domain })
  },
})

export const brandQuery = atom<string>({
  key: key('brand-query'),
  default: '',
})

export const mockBrands = atom<AlphamapBrandLogo[] | undefined>({
  key: key('mockBrands'),
  default: undefined,
})

export const allBrands = mockableSelector<AlphamapBrandLogo[]>({
  key: key('all-brands'),
  get: async () => {
    return brandLogosClient.getAllBrandLogos()
  },
  mock: mockBrands,
})

export const uniqueBrands = mockableSelector<AlphamapBrandLogo[]>({
  key: key('unique-brands'),
  get: async () => {
    return brandLogosClient.getUniqueBrandLogos()
  },
  mock: mockBrands,
})

export const brandLogosInRadius = selectorFamily<
  BrandLogosInRadiusResponse | undefined,
  BrandLogosInRadiusRequest | undefined
>({
  key: key('brand-logos-in-radius'),
  get: (request) => async () => {
    if (!request) {
      return undefined
    }
    return await brandLogosClient.getAlphamapBrandLogosInRadius(request)
  },
})

export const tfBrands = mockableSelector<AlphamapBrandLogo[]>({
  key: key('tf-brands'),
  get: async () => {
    const brandsMap = await brandLogosClient.getTenantFinderBrandLogos()
    const brands: AlphamapBrandLogo[] = Object.keys(brandsMap).map((key) => ({
      brandId: key,
      name: brandsMap[key].name,
      s3Key: '',
    }))
    return brands
  },
  mock: mockBrands,
})

export const brandLogoUrl = selectorFamily<string | null, string | undefined | null>({
  key: key('brand-logo-url'),
  get: (brandId) => async () => {
    if (!brandId) {
      return null
    }
    try {
      const logo = await brandLogosClient.getAlphamapBrandLogoById(brandId)
      const imageUrl = logo.logoUrl ? `${BASE_IMAGE_URL}${logo.logoUrl}` : null
      return imageUrl
    } catch {
      return null
    }
  },
})

export const amBrands = selector<AMBrand[]>({
  key: key('am-brands'),
  get: async () => {
    try {
      const brands = await brandsClient.getAlphamapBrands()
      return brands
    } catch (error) {
      browserLogger.error(error)
      return []
    }
  },
})
